import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Chip,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Box,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Icon,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import LaunchIcon from "@material-ui/icons/Launch";
import { Link } from "react-router-dom";
import { AccountCircle, Add } from "@material-ui/icons";
import WaveTitle from "../SolutionGenerator/WaveTitle";
import SearchBar from "material-ui-search-bar";
import Button from "components/CustomButtons/Button.js";
import { getAllPromptsAPI } from "../../APIs/PromptHubUtility";
import { Auth } from "aws-amplify";
import { fetchUserAPI } from "views/APIs/APIUtility";

const useStyles = makeStyles((theme) => ({
  appBar: {
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  toolbar: {
    display: "grid",
    gridTemplateColumns: "1fr auto auto",
    gap: theme.spacing(2),
    alignItems: "center",
  },
  searchField: {
    background: theme.palette.background.default,
    borderRadius: 12,
  },
  filterRow: {
    display: "grid",
    gridTemplateColumns: "30px 30px 1fr 240px 160px auto",
    gap: theme.spacing(2),
    margin: `${theme.spacing(3)}px 0`,
  },
  card: {
    borderRadius: 16,
    boxShadow: "0 1px 2px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.06)",
    height: "100%",
    transition: "transform 120ms ease, box-shadow 120ms ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 2px 4px rgba(0,0,0,0.08), 0 12px 28px rgba(0,0,0,0.12)",
    },
  },
  header: {
    paddingBottom: 0,
  },
  chipRow: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  subtle: {
    color: theme.palette.text.secondary,
  },
  gridList: {
    marginTop: theme.spacing(1),
  },
  empty: {
    textAlign: "center",
    padding: theme.spacing(8),
    color: theme.palette.text.secondary,
  },
  row: {
    borderRadius: 8,
    transition: "background 120ms ease",
    backgroundColor: "white",
    cursor: "pointer",
    "&:hover": {
      background: theme.palette.action.hover,
    },
    paddingRight: theme.spacing(10), // space so text doesn't hide behind secondary action
  },
  avatar: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  title: { fontWeight: 600 },
  subheader: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
  },
  descriptionClamp: {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.75),
  },
  metaGrid: {
    marginTop: theme.spacing(1),
  },
  subtleLabel: {
    color: theme.palette.text.secondary,
  },
  createdBy: {
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(0.5),
    "& svg": { marginRight: theme.spacing(0.5) },
  },
  secondaryActionWrap: {
    right: theme.spacing(1),
  },
}));

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function initials(name) {
  if (!name) return "?";
  const parts = name.replace("_", " ").split(" ").filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function useDebouncedValue(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

/***********************************
 * Sorting helpers
 ***********************************/
const SORTERS = {
  name_asc: (a, b) => a.name.localeCompare(b.name),
  name_desc: (a, b) => b.name.localeCompare(a.name),
  created_at_asc: (a, b) => new Date(a.created_at) - new Date(b.created_at),
  created_at_desc: (a, b) => new Date(b.created_at) - new Date(a.created_at),
  updated_at_asc: (a, b) => new Date(a.updated_at) - new Date(b.updated_at),
  updated_at_desc: (a, b) => new Date(b.updated_at) - new Date(a.updated_at),
};

/***********************************
 * Page: Prompts Landing
 ***********************************/
export default function PromptHub() {
  if (localStorage.getItem("userCategory") !== "super_admin") {
    window.location.href = "/create/landing-page";
  }

  const classes = useStyles();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Controls
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [appFilter, setAppFilter] = useState("all");
  const [sortKey, setSortKey] = useState("updated_at_desc");
  const [view, setView] = useState("grid");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getAllPromptsAPI();
        setPrompts(data);
      } catch (e) {
        setError("Failed to fetch prompts. Try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const applicationNames = useMemo(() => {
    const set = new Set(prompts.map((p) => p.application_name).filter(Boolean));
    return ["all", ...Array.from(set).sort()];
  }, [prompts]);

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    let list = prompts;
    if (q) {
      list = list.filter((p) => {
        return [p.name, p.description, p.application_name]
          .filter(Boolean)
          .some((f) => f.toLowerCase().includes(q));
      });
    }
    if (appFilter !== "all") {
      list = list.filter((p) => p.application_name === appFilter);
    }
    const sorter = SORTERS[sortKey] || SORTERS.updated_at_desc;
    return [...list].sort(sorter);
  }, [prompts, debouncedSearch, appFilter, sortKey]);

  return (
    <>
      <Divider style={{ marginBottom: "1rem" }} />
      <Typography variant="h4">
        <WaveTitle text="PromptHub" />
      </Typography>
      <Typography variant="body1">
        Edit and manage your prompts here.
      </Typography>
      <Divider style={{ marginTop: "1rem", marginBottom: "1rem" }} />

      <Box>
        <Box className={classes.filterRow}>
          <Icon
            style={{
              cursor: "pointer",
              marginTop: "15px",
              marginRight: "10px",
              ...(view === "grid"
                ? {
                    backgroundColor: "rgb(32, 103, 123, 0.3)",
                    borderRadius: "10%",
                  }
                : {}),
            }}
            onClick={() => setView("grid")}
          >
            apps
          </Icon>
          <Icon
            style={{
              cursor: "pointer",
              marginTop: "15px",
              marginRight: "10px",
              ...(view === "list"
                ? {
                    backgroundColor: "rgb(32, 103, 123, 0.3)",
                    borderRadius: "10%",
                  }
                : {}),
            }}
            onClick={() => setView("list")}
          >
            view_list
          </Icon>

          <SearchBar
            value={search}
            onChange={(newValue) => setSearch(newValue)}
            onCancelSearch={() => setSearch("")}
            placeholder="Search by name, description, or application…"
            style={{ width: "100%", marginRight: "10px" }}
          />

          <FormControl
            variant="outlined"
            size="small"
            style={{
              marginRight: "10px",
              marginLeft: "10px",
              marginTop: "5px",
            }}
          >
            <InputLabel id="app-filter-label">Application</InputLabel>
            <Select
              labelId="app-filter-label"
              label="Application"
              value={appFilter}
              onChange={(e) => setAppFilter(e.target.value)}
            >
              {applicationNames.map((name) => (
                <MenuItem key={name} value={name}>
                  {name === "all" ? "All applications" : name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            variant="outlined"
            size="small"
            style={{ marginTop: "5px", marginRight: "10px" }}
          >
            <InputLabel id="sorter-label">
              {/* <SortIcon style={{ marginRight: 6 }} /> */}
              Sort by
            </InputLabel>
            <Select
              labelId="sorter-label"
              label="Sort by"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
            >
              <MenuItem value="name_asc">Name (A→Z)</MenuItem>
              <MenuItem value="name_desc">Name (Z→A)</MenuItem>
              <Divider />
              <MenuItem value="created_at_desc">Created (new → old)</MenuItem>
              <MenuItem value="created_at_asc">Created (old → new)</MenuItem>
              <Divider />
              <MenuItem value="updated_at_desc">Updated (new → old)</MenuItem>
              <MenuItem value="updated_at_asc">Updated (old → new)</MenuItem>
            </Select>
          </FormControl>

          {/* <Button
                        startIcon={<Add />}
                        variant="outlined"
                        size="small"
                        style={{ marginTop: "1px" }}
                        color="quCreateColor"
                    >
                        New Prompt
                    </Button> */}
        </Box>

        {error && (
          <Box color="error.main" mb={2}>
            {error}
          </Box>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" mt={8}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Box className={classes.empty}>
            <Typography variant="h6">No prompts found</Typography>
            <Typography className={classes.subtle}>
              Try adjusting your search, filters, or sorting.
            </Typography>
          </Box>
        ) : view === "grid" ? (
          <Grid container spacing={3} className={classes.gridList}>
            {filtered.map((p) => (
              <Grid item xs={12} sm={6} md={4} key={p._id}>
                <Card
                  className={classes.card}
                  onClick={() =>
                    (window.location.href = `/create/prompt-hub/prompt-editor/${p._id}`)
                  }
                >
                  <CardHeader
                    className={classes.header}
                    avatar={
                      <Tooltip title={p.prompt_name || p.create_by}>
                        <Avatar className={classes.avatar}>
                          {initials(p.name)}
                        </Avatar>
                      </Tooltip>
                    }
                    action={
                      <Tooltip title="Open editor">
                        <IconButton
                          component={Link}
                          to={`/create/prompt-hub/prompt-editor/${p._id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <LaunchIcon />
                        </IconButton>
                      </Tooltip>
                    }
                    title={
                      <Typography
                        variant="subtitle1"
                        style={{ fontWeight: 600 }}
                      >
                        {p.name.replaceAll("_", " ")}
                      </Typography>
                    }
                    subheader={
                      <span className={classes.subtle}>
                        {p.application_name}
                      </span>
                    }
                  />
                  <CardContent>
                    <Typography variant="body2" gutterBottom>
                      <Tooltip title={p.description}>
                        <Typography variant="body2">
                          {p.description.slice(0, 150) +
                            (p.description.length > 150 ? "..." : "")}
                        </Typography>
                      </Tooltip>
                    </Typography>
                    <div className={classes.chipRow}>
                      <Chip
                        size="small"
                        label={`Deployed v${p.deployed_version ?? "-"}`}
                        style={{
                          marginRight: "8px",
                          backgroundColor: "#20677b",
                          color: "white",
                        }}
                      />
                      <Chip
                        size="small"
                        label={`Required Fields: ${
                          p.required_fields?.length ?? 0
                        }`}
                      />
                    </div>
                    <Divider style={{ margin: "12px 0" }} />
                    <Grid container spacing={1}>
                      <Grid item xs={4}>
                        <Typography
                          variant="caption"
                          className={classes.subtle}
                        >
                          Created on
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(p.created_at)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography
                          variant="caption"
                          className={classes.subtle}
                        >
                          Updated on
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(p.updated_at)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography
                          variant="caption"
                          className={classes.subtle}
                        >
                          Created by
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <AccountCircle
                            fontSize="small"
                            style={{ marginRight: "4px" }}
                          />
                          {p.created_by}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <List disablePadding>
            {filtered.map((p, idx) => {
              const to = `/create/prompt-hub/prompt-editor/${p._id}`;
              const primary = (
                <div>
                  <Typography variant="subtitle1" className={classes.title}>
                    {p.name.replaceAll("_", " ")}
                  </Typography>
                  <Typography variant="body2" className={classes.subheader}>
                    {p.application_name}
                  </Typography>
                </div>
              );

              const secondary = (
                <div>
                  {/* Description with tooltip and clamp */}
                  <Tooltip title={p.description || ""} placement="top" arrow>
                    <Typography
                      variant="body2"
                      className={classes.descriptionClamp}
                    >
                      {p.description?.slice(0, 150)}
                      {p.description?.length > 150 ? "…" : ""}
                    </Typography>
                  </Tooltip>

                  {/* Chips */}
                  <div className={classes.chipRow}>
                    <Chip
                      size="small"
                      label={`Deployed v${p.deployed_version ?? "-"}`}
                      style={{
                        backgroundColor: "#20677b",
                        color: "white",
                        marginRight: "8px",
                      }}
                    />
                    <Chip
                      size="small"
                      label={`Latest v${p.latest_version ?? "-"}`}
                      style={{ marginRight: "8px" }}
                    />
                    <Chip
                      size="small"
                      label={`Required Fields: ${
                        p.required_fields?.length ?? 0
                      }`}
                      style={{ marginRight: "8px" }}
                    />
                  </div>

                  {/* Meta */}
                  <Grid container spacing={2} className={classes.metaGrid}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography
                        variant="caption"
                        className={classes.subtleLabel}
                      >
                        Created on
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(p.created_at)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography
                        variant="caption"
                        className={classes.subtleLabel}
                      >
                        Updated on
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(p.updated_at)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="caption"
                        className={classes.subtleLabel}
                      >
                        Created by
                      </Typography>
                      <div className={classes.createdBy}>
                        <AccountCircle fontSize="small" />
                        <Typography variant="body2">{p.created_by}</Typography>
                      </div>
                    </Grid>
                  </Grid>
                </div>
              );

              return (
                <React.Fragment key={p._id}>
                  <ListItem
                    button
                    className={classes.row}
                    onClick={() => {
                      window.location.href = `/create/prompt-hub/prompt-editor/${p._id}`;
                    }}
                    alignItems="flex-start"
                  >
                    <ListItemAvatar>
                      <Tooltip title={p.prompt_name || p.create_by} arrow>
                        <Avatar className={classes.avatar}>
                          {initials(p.name)}
                        </Avatar>
                      </Tooltip>
                    </ListItemAvatar>

                    <ListItemText primary={primary} secondary={secondary} />

                    {/* Secondary action (doesn't steal the row click) */}
                    <ListItemSecondaryAction
                      className={classes.secondaryActionWrap}
                    >
                      <Tooltip title="Open editor" arrow>
                        <IconButton
                          edge="end"
                          component={Link}
                          to={to}
                          onClick={(e) => e.stopPropagation()}
                          aria-label="open-editor"
                        >
                          <LaunchIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>

                  {/* Divider between rows */}
                  {idx < filtered.length - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>
    </>
  );
}
